// Dealership settings (docs/decisions/dealership-settings.md): the built-in
// defaults, how a GET /api/settings payload resolves into what the UI reads,
// the aging buckets derived from the three boundaries, and the client-side
// validation that mirrors the backend registry.
//
// With no override (or no answer from the API) every value below equals
// today's hard-coded behaviour, so the UI looks and works exactly as before.

export const SETTING_KEYS = Object.freeze({
    agingBoundaries: 'aging.boundaries',
    criticalDays: 'aging.critical_days',
    gapNeutral: 'pricing.gap_neutral_pct',
    gapWarn: 'pricing.gap_warn_pct',
    gapCritical: 'pricing.gap_critical_pct',
    peerOverpriced: 'pricing.peer_overpriced_pct',
    marginFloor: 'margin.floor_pct',
    userFloorMin: 'margin.user_floor_min_pct',
    userFloorMax: 'margin.user_floor_max_pct',
    apr: 'costs.floor_plan_apr_pct',
});

export const DEFAULT_VALUES = Object.freeze({
    'aging.boundaries': Object.freeze([30, 90, 180]),
    'aging.critical_days': 90,
    'pricing.gap_neutral_pct': 2,
    'pricing.gap_warn_pct': 5,
    'pricing.gap_critical_pct': 10,
    'pricing.peer_overpriced_pct': 15,
    'margin.floor_pct': 10,
    'costs.floor_plan_apr_pct': 7,
    'margin.user_floor_min_pct': 0,
    'margin.user_floor_max_pct': 50,
});

// The registry as the UI needs it when the API sends no schema. Same keys,
// bounds, units, groups and wording as app/settings/registry.py.
// The UI shows the labels and descriptions below (checked against what the
// code does, QA S-7); bounds and types come from the API schema when given.
export const DEFAULT_SCHEMA = Object.freeze([
    {
        key: 'aging.boundaries', type: 'int[3]', default: [30, 90, 180], min: 1, max: 730, unit: 'days',
        scope: 'dealership', group: 'aging', label: 'Aging boundaries',
        description: 'Where each aging bucket ends: the inventory tiles, filters and dots and the Branches aging figures. There are always four buckets; the last is everything past the third boundary and is the "aged" count in Needs attention.',
    },
    {
        key: 'aging.critical_days', type: 'int', default: 90, min: 1, max: 730, unit: 'days',
        scope: 'dealership', group: 'aging', label: 'Critical from',
        description: 'On the Branches page, units in stock longer than this count as critical, and day figures past it turn amber.',
    },
    {
        key: 'pricing.gap_neutral_pct', type: 'number', default: 2, min: 0, max: 20, unit: '%',
        scope: 'dealership', group: 'pricing', label: 'In line up to',
        description: 'The lot value Gap figure stays uncoloured within this distance of the AI estimate. Unit chips count as in line up to "Above AI estimate from".',
    },
    {
        key: 'pricing.gap_warn_pct', type: 'number', default: 5, min: 0, max: 30, unit: '%',
        scope: 'dealership', group: 'pricing', label: 'Above AI estimate from',
        description: 'Asking more than this over the AI estimate shows as Above and raises the Above AI Estimate signal; this far below shows as Below. Also splits the Branches price alignment.',
    },
    {
        key: 'pricing.gap_critical_pct', type: 'number', default: 10, min: 0, max: 50, unit: '%',
        scope: 'dealership', group: 'pricing', label: 'Well above AI estimate from',
        description: 'Asking more than this over the AI estimate shows as Well above (red).',
    },
    {
        key: 'pricing.peer_overpriced_pct', type: 'number', default: 15, min: 1, max: 100, unit: '%',
        scope: 'dealership', group: 'pricing', label: 'Overpriced vs peers from',
        description: 'Asking more than this over the median of the same make, model and year raises the Overpriced vs Peers signal.',
    },
    {
        key: 'costs.floor_plan_apr_pct', type: 'number', default: 7, min: 0, max: 25, unit: '%',
        scope: 'dealership', group: 'costs', label: 'Floor plan APR',
        description: 'Annual floor plan rate for units without a rate of their own: daily carrying cost (on the true cost), cost to date, margin after carrying cost and the AI brief. It does not change the true cost.',
    },
    {
        key: 'margin.floor_pct', type: 'number', default: 10, min: 0, max: 50, unit: '%',
        scope: 'dealership_default_user_override', group: 'costs', label: 'Margin floor',
        description: 'Gross margin at or above this shows green, below it amber (unit pages and the margin calculator); the AI brief measures margin runway against it.',
    },
    {
        key: 'margin.user_floor_min_pct', type: 'number', default: 0, min: 0, max: 50, unit: '%',
        scope: 'dealership', group: 'costs', label: 'Personal margin floor: lowest allowed',
        description: 'The lowest margin floor a user may set for themselves in My preferences (their unit pages only). At most the margin floor.',
    },
    {
        key: 'margin.user_floor_max_pct', type: 'number', default: 50, min: 0, max: 50, unit: '%',
        scope: 'dealership', group: 'costs', label: 'Personal margin floor: highest allowed',
        description: 'The highest margin floor a user may set for themselves. At least the margin floor. A personal value outside the range is ignored.',
    },
]);

// Tones by bucket id (1 freshest … 4 oldest), shared by every aging dot,
// tile and chip in the redesign.
export const AGING_TONES_BY_ID = Object.freeze({ 1: 'sage', 2: 'slate', 3: 'amber', 4: 'red' });
// The unit page's text tones by bucket id.
export const AGING_TEXT_TONES_BY_ID = Object.freeze({ 1: 'pos', 2: 'info', 3: 'warn', 4: 'neg' });
// Today's v1 colours by bucket id.
export const AGING_COLORS_BY_ID = Object.freeze({ 1: '#15803d', 2: '#1d4ed8', 3: '#b45309', 4: '#b91c1c' });
export const AGING_BG_BY_ID = Object.freeze({ 1: '#dcfce7', 2: '#dbeafe', 3: '#fef3c7', 4: '#fee2e2' });
// The KPI keys the inventory stats and the /combined branch rows use.
export const AGING_KEYS_BY_ID = Object.freeze({ 1: 'fresh', 2: 'normal', 3: 'slow', 4: 'critical' });
export const OLDEST_BUCKET_ID = 4;
export const AGING_BUCKET_IDS = Object.freeze([1, 2, 3, 4]);

// The labels the backend used before settings existed. Saved filters and
// shared links carry them; each maps to its bucket id.
export const LEGACY_AGING_LABELS = Object.freeze({
    '0–30 days': 1, '31–90 days': 2, '91–180 days': 3, '180+ days': 4,
});
// Short URL aliases (?aging=0-30, 180+, …), compared after dash folding.
const LEGACY_AGING_ALIASES = Object.freeze({
    '0-30': 1, '31-90': 2, '91-180': 3, '180+': 4, 180: 4,
});

// ── Aging buckets ────────────────────────────────────────────────────────

/** The four buckets for three ascending boundaries; labels use an en dash. */
export const buildAgingBuckets = (boundaries = DEFAULT_VALUES['aging.boundaries']) => {
    const [b1, b2, b3] = boundaries;
    const ranges = [[0, b1], [b1 + 1, b2], [b2 + 1, b3], [b3 + 1, null]];
    return ranges.map(([min, max], i) => {
        const id = i + 1;
        const label = max === null ? `${b3}+ days` : `${min}–${max} days`;
        return Object.freeze({
            id,
            key: AGING_KEYS_BY_ID[id],
            label,
            // KPI tile wording ("0–30 Days"), as the tiles read today.
            tileLabel: max === null ? `${b3}+ Days` : `${min}–${max} Days`,
            // Legend wording ("0–30", "180+ days").
            short: max === null ? `${b3}+ days` : `${min}–${max}`,
            min,
            max,
            tone: AGING_TONES_BY_ID[id],
        });
    });
};

/** Bucket id (1–4) for a number of days in stock, or null when unknown. */
export const agingBucketIdForDays = (days, boundaries = DEFAULT_VALUES['aging.boundaries']) => {
    if (days === null || days === undefined || days === '') return null;
    const d = Number(days);
    if (!Number.isFinite(d)) return null;
    const [b1, b2, b3] = boundaries;
    if (d <= b1) return 1;
    if (d <= b2) return 2;
    if (d <= b3) return 3;
    return 4;
};

const foldLabel = (s) => String(s).trim().toLowerCase().replace(/[–—]/g, '-').replace(/\s+/g, ' ');

/**
 * One aging filter value (an id, a legacy label, a current label, a short
 * alias) → its bucket id, or null when it names nothing.
 */
export const agingIdOf = (value, buckets = DEFAULT_AGING.buckets) => {
    if (typeof value === 'number') return AGING_BUCKET_IDS.includes(value) ? value : null;
    if (typeof value !== 'string') return null;
    const raw = value.trim();
    if (/^[1-4]$/.test(raw)) return Number(raw);
    if (LEGACY_AGING_LABELS[raw]) return LEGACY_AGING_LABELS[raw];
    const folded = foldLabel(raw);
    const legacy = Object.entries(LEGACY_AGING_LABELS).find(([label]) => foldLabel(label) === folded);
    if (legacy) return legacy[1];
    const current = (buckets || []).find((b) => foldLabel(b.label) === folded || foldLabel(b.tileLabel || '') === folded);
    if (current) return current.id;
    // "180+" (or "180 " when a bare "+" was read back as a space), "0-30 days".
    const alias = folded.replace(/\s*days?$/, '').trim();
    if (LEGACY_AGING_ALIASES[alias]) return LEGACY_AGING_ALIASES[alias];
    return null;
};

/** A saved / restored / linked aging selection → unique bucket ids. */
export const normalizeAgingSelection = (value, buckets = DEFAULT_AGING.buckets) => {
    if (value === null || value === undefined || value === 'All') return [];
    const list = Array.isArray(value) ? value : [value];
    return [...new Set(list.map((v) => agingIdOf(v, buckets)).filter(Boolean))];
};

/**
 * A unit's bucket id: the backend's DaysInStockBucketId when present, else
 * computed from DaysInStock with the boundaries, else its legacy label.
 */
export const unitAgingBucketId = (unit, aging = DEFAULT_AGING) => {
    const id = unit?.DaysInStockBucketId;
    if (typeof id === 'number' && AGING_BUCKET_IDS.includes(id)) return id;
    if (typeof id === 'string' && /^[1-4]$/.test(id)) return Number(id);
    const fromDays = agingBucketIdForDays(unit?.DaysInStock, aging.boundaries);
    if (fromDays) return fromDays;
    return unit?.DaysInStockBucket ? agingIdOf(unit.DaysInStockBucket, aging.buckets) : null;
};

/** The bucket a unit falls in, or null. */
export const unitAgingBucket = (unit, aging = DEFAULT_AGING) => {
    const id = unitAgingBucketId(unit, aging);
    return id ? aging.buckets.find((b) => b.id === id) || null : null;
};

/** The bucket label to show for a unit ("" when unknown). */
export const unitAgingLabel = (unit, aging = DEFAULT_AGING) => (
    unitAgingBucket(unit, aging)?.label || unit?.DaysInStockBucket || ''
);

/** A unit's v2 aging tone ('sage' … 'red'), 'grey' when unknown. */
export const unitAgingTone = (unit, aging = DEFAULT_AGING) => AGING_TONES_BY_ID[unitAgingBucketId(unit, aging)] || 'grey';

/** Units per bucket id: { 1: n, 2: n, 3: n, 4: n }. */
export const countAgingBuckets = (units, aging = DEFAULT_AGING, idOf = unitAgingBucketId) => {
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0 };
    (units || []).forEach((u) => {
        const id = idOf(u, aging);
        if (id) counts[id] += 1;
    });
    return counts;
};

/**
 * A days-in-stock tone for tables and cards: 'crit' past the oldest
 * boundary, 'warn' past the critical days, 'ok' otherwise.
 */
export const daysInStockSeverity = (days, aging = DEFAULT_AGING) => {
    const d = Number(days) || 0;
    if (d > aging.boundaries[2]) return 'crit';
    if (d > aging.criticalDays) return 'warn';
    return 'ok';
};

// ── Validation (mirrors the registry) ───────────────────────────────────

const schemaOf = (key, schema) => (schema || DEFAULT_SCHEMA).find((s) => s.key === key)
    || DEFAULT_SCHEMA.find((s) => s.key === key);

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

/**
 * Per-key error messages for a full set of values (empty object = valid).
 * Bounds and types per key; aging boundaries strictly ascending; the gap
 * thresholds neutral < warn < critical.
 */
export const validateSettings = (values, schema = DEFAULT_SCHEMA) => {
    const errors = {};
    Object.keys(DEFAULT_VALUES).forEach((key) => {
        const def = schemaOf(key, schema);
        const v = values?.[key];
        const { min, max } = def;
        if (key === SETTING_KEYS.agingBoundaries) {
            if (!Array.isArray(v) || v.length !== 3 || !v.every((n) => isNum(n) && Number.isInteger(n))) {
                errors[key] = 'Enter three whole numbers of days.';
                return;
            }
            if (v.some((n) => n < min || n > max)) {
                errors[key] = `Each boundary must be between ${min} and ${max} days.`;
                return;
            }
            if (!(v[0] < v[1] && v[1] < v[2])) errors[key] = 'Boundaries must go up: each one larger than the one before.';
            return;
        }
        if (!isNum(v)) { errors[key] = 'Enter a number.'; return; }
        if (def.type === 'int' && !Number.isInteger(v)) { errors[key] = 'Enter a whole number.'; return; }
        if (v < min || v > max) errors[key] = `Must be between ${min} and ${max}${def.unit === '%' ? ' %' : ` ${def.unit}`}.`;
    });
    const neutral = values?.[SETTING_KEYS.gapNeutral];
    const warn = values?.[SETTING_KEYS.gapWarn];
    const critical = values?.[SETTING_KEYS.gapCritical];
    if (!errors[SETTING_KEYS.gapWarn] && isNum(neutral) && isNum(warn) && !(warn > neutral)) {
        errors[SETTING_KEYS.gapWarn] = 'Must be larger than "In line up to".';
    }
    if (!errors[SETTING_KEYS.gapCritical] && isNum(warn) && isNum(critical) && !(critical > warn)) {
        errors[SETTING_KEYS.gapCritical] = 'Must be larger than "Above AI estimate from".';
    }
    // The personal margin floor range holds the dealership floor (decision 9).
    const floor = values?.[SETTING_KEYS.marginFloor];
    const lo = values?.[SETTING_KEYS.userFloorMin];
    const hi = values?.[SETTING_KEYS.userFloorMax];
    if (!errors[SETTING_KEYS.marginFloor] && isNum(floor)) {
        if (!errors[SETTING_KEYS.userFloorMin] && isNum(lo) && lo > floor) {
            errors[SETTING_KEYS.userFloorMin] = 'Must be at most the margin floor.';
        }
        if (!errors[SETTING_KEYS.userFloorMax] && isNum(hi) && hi < floor) {
            errors[SETTING_KEYS.userFloorMax] = 'Must be at least the margin floor.';
        }
    }
    return errors;
};

// ── Resolving an API payload ────────────────────────────────────────────

const sameValue = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/** The API schema's bounds with the UI's own label and description per key. */
export const mergeSchema = (apiSchema) => DEFAULT_SCHEMA.map((local) => {
    const api = Array.isArray(apiSchema) ? apiSchema.find((d) => d?.key === local.key) : null;
    return api ? { ...local, ...api, label: local.label, description: local.description } : local;
});

/**
 * The effective values: the payload's values over the defaults, each key
 * kept only when it passes validation on its own (and the cross-key rules
 * as a set); anything else falls back to the default, so a bad payload can
 * never break the UI.
 */
export const effectiveValues = (payloadValues) => {
    const merged = { ...DEFAULT_VALUES };
    if (payloadValues && typeof payloadValues === 'object') {
        Object.keys(DEFAULT_VALUES).forEach((key) => {
            if (payloadValues[key] !== undefined && payloadValues[key] !== null) merged[key] = payloadValues[key];
        });
    }
    const errors = validateSettings(merged);
    Object.keys(errors).forEach((key) => { merged[key] = DEFAULT_VALUES[key]; });
    // The gap scale is one rule: if the three no longer ascend after the
    // per-key fallback, use the default scale.
    if (Object.keys(validateSettings(merged)).length) {
        merged[SETTING_KEYS.gapNeutral] = DEFAULT_VALUES[SETTING_KEYS.gapNeutral];
        merged[SETTING_KEYS.gapWarn] = DEFAULT_VALUES[SETTING_KEYS.gapWarn];
        merged[SETTING_KEYS.gapCritical] = DEFAULT_VALUES[SETTING_KEYS.gapCritical];
    }
    // Likewise the personal floor range: the default range when it no longer holds.
    if (Object.keys(validateSettings(merged)).length) {
        merged[SETTING_KEYS.userFloorMin] = DEFAULT_VALUES[SETTING_KEYS.userFloorMin];
        merged[SETTING_KEYS.userFloorMax] = DEFAULT_VALUES[SETTING_KEYS.userFloorMax];
    }
    return merged;
};

/** The aging part of resolved settings, from values. */
export const agingFromValues = (values) => {
    const boundaries = [...values[SETTING_KEYS.agingBoundaries]];
    return Object.freeze({
        boundaries,
        buckets: buildAgingBuckets(boundaries),
        criticalDays: values[SETTING_KEYS.criticalDays],
    });
};

/** Gap thresholds as fractions of the AI value (the lotValue scale). */
export const gapThresholdsFromValues = (values) => Object.freeze({
    neutral: values[SETTING_KEYS.gapNeutral] / 100,
    warn: values[SETTING_KEYS.gapWarn] / 100,
    critical: values[SETTING_KEYS.gapCritical] / 100,
});

/**
 * GET /api/settings (or the admin GET) → what the UI reads. A null payload
 * gives the built-in defaults.
 */
export const resolveSettings = (payload) => {
    const values = effectiveValues(payload?.values);
    const overridden = Array.isArray(payload?.overridden)
        ? payload.overridden.filter((k) => k in DEFAULT_VALUES)
        : Object.keys(DEFAULT_VALUES).filter((k) => !sameValue(values[k], DEFAULT_VALUES[k]));
    return {
        orgId: payload?.org_id ?? null,
        tenantSlug: payload?.tenant_slug ?? null,
        version: typeof payload?.version === 'number' ? payload.version : null,
        values,
        defaults: { ...DEFAULT_VALUES, ...(payload?.defaults || {}) },
        overridden,
        schema: mergeSchema(payload?.schema),
        aging: agingFromValues(values),
        criticalDays: values[SETTING_KEYS.criticalDays],
        gapThresholds: gapThresholdsFromValues(values),
        peerOverpricedPct: values[SETTING_KEYS.peerOverpriced],
        marginFloorPct: values[SETTING_KEYS.marginFloor],
        userFloorRange: { min: values[SETTING_KEYS.userFloorMin], max: values[SETTING_KEYS.userFloorMax] },
        aprPct: values[SETTING_KEYS.apr],
        canEdit: Boolean(payload?.can_edit),
        isDefault: !payload,
    };
};

export const DEFAULT_SETTINGS = Object.freeze(resolveSettings(null));
export const DEFAULT_AGING = DEFAULT_SETTINGS.aging;

/** The thresholds the signal tooltips quote, from resolved settings. */
export const signalThresholds = (settings = DEFAULT_SETTINGS) => ({
    peerOverpricedPct: settings.peerOverpricedPct,
    gapWarnPct: settings.values[SETTING_KEYS.gapWarn],
});

// ── Timestamps (history) ─────────────────────────────────────────────────

/**
 * An API timestamp as a Date. The backend stores UTC; a timestamp without
 * a timezone ("2026-10-13T09:11:39.104964") is read as UTC, not local time.
 * Null when it does not parse.
 */
export const parseApiTimestamp = (value) => {
    if (!value || typeof value !== 'string') return null;
    let s = value.trim().replace(' ', 'T');
    // JS keeps milliseconds only.
    s = s.replace(/(\.\d{3})\d+/, '$1');
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(s)) s += 'Z';
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
};

/** "Oct 13, 2026, 11:11 AM" in the viewer's local time; "—" when missing. */
export const formatSettingTime = (value) => {
    const d = parseApiTimestamp(value);
    if (!d) return value ? String(value) : '—';
    return d.toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
};

// ── "Critical" on Branches (QA P2-5) ────────────────────────────────────
// Two different figures used to share the word: the aging tile's share past
// the last boundary (named by the last bucket's label, e.g. "180+ days") and
// the category / Market count past `aging.critical_days` (named below).

/** "Critical (> 90 d)": units in stock longer than the critical days. */
export const criticalLabel = (criticalDays) => `Critical (> ${criticalDays} d)`;

/** The tooltip for that figure, naming the setting it follows. */
export const criticalTip = (criticalDays) => (
    `Units in stock for more than ${criticalDays} days (the dealership setting "Critical from").`
);
